import { Component } from '@angular/core';

import { TIN_STATUS } from '../manage-access.constant';
import { ManageUserAccessFacade } from '../+state/manage-user-access.facade';
import {
  User,
  EditedUser,
  TinAccess,
  EntitlementRole,
} from '../manage-access.model';

import { DialogExplainAccessComponent } from '../dialog-explain-access/dialog-explain-access.component';
import { DialogAssignmentRemoveUserComponent } from '../dialog-assignment-remove-user/dialog-assignment-remove-user.component';

import { MatDialog } from '@angular/material/dialog';
import isEqual from 'lodash/isEqual';
import { PhoneNumberPipe } from '@cigna/chcp/shared/ui';
import { Router, ActivatedRoute } from '@angular/router';
import { FeatureTogglesFacade } from '@cigna/features';
import { LOB_BEHAVIORAL, LOB_MEDBH, LOB_MEDICAL } from '@cigna/chcp/auth/util';
import { AuthFacade } from '@cigna/chcp/auth/data-access';
import { combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
  selector: 'cigna-page-assign-access-individual',
  templateUrl: './page-assign-access-individual.component.html',
  styleUrls: ['./page-assign-access-individual.component.scss'],
})
export class PageAssignAccessIndividualComponent {
  userLob$ = this.authFacade.lob$;
  LOB_MEDBH = LOB_MEDBH;
  constructor(
    private manageAccessFacade: ManageUserAccessFacade,
    public dialog: MatDialog,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private featureTogglesFacade: FeatureTogglesFacade,
    private authFacade: AuthFacade,
  ) {}

  TIN_STATUS = TIN_STATUS;

  allTins$ = this.manageAccessFacade.allTins$;
  allEntitlements$ = this.manageAccessFacade.allEntitlements$;
  selectedUsers$ = this.manageAccessFacade.selectedUsers$;
  selectedUsersLength$ = this.manageAccessFacade.selectedUsersLength$;
  changesValid$ = this.manageAccessFacade.individualSelectedEntitlementsValid$;
  hiddenSteps$ = this.manageAccessFacade.hiddenSteps$;
  steps$ = this.manageAccessFacade.steps$;

  removeUser(user: User): void {
    this.manageAccessFacade.unselectUserToEdit(user);
  }

  trackByUserId(_index: number, user: User): string {
    return user.id;
  }

  generateUserHeaderLabel(user: User): string {
    if (user.phone) {
      const formattedPhone: string = new PhoneNumberPipe().transform(
        user.phone,
      );
      return `${user.lastName}, ${user.firstName} | ${user.email} | ${formattedPhone}`;
    }
    return `${user.lastName}, ${user.firstName} | ${user.email}`;
  }

  generateTinHeaderLabel(tin: TinAccess, user: User): string {
    let accessLabel = '';

    if (user.tins) {
      const activeTin = user.tins.find((tinObj) => tinObj.tin === tin.tin);

      const isAwaiting =
        activeTin !== undefined && activeTin.status === TIN_STATUS.unassigned;

      accessLabel = isAwaiting ? ` - Awaiting approval` : '';
    }
    const lobName =
      tin.lob === LOB_MEDBH
        ? ' - Medical/Behavioral'
        : tin.lob === LOB_MEDICAL
        ? ' - Medical'
        : tin.lob === LOB_BEHAVIORAL
        ? ' - Behavioral'
        : '';

    return tin.name !== '' && tin.name !== ' '
      ? `${tin.tin} | (${tin.name})${accessLabel}${lobName}`
      : `${tin.tin}${accessLabel}${lobName}`;
  }

  openExplainAccessDialog() {
    this.dialog.open(DialogExplainAccessComponent, {
      autoFocus: false,
      width: '700px',
    });
  }

  openRemoveUserFromEditingDialog(user: User) {
    const dialogRef = this.dialog.open(DialogAssignmentRemoveUserComponent, {
      data: {
        user,
      },
      autoFocus: false,
      width: '400px',
    });

    dialogRef.componentInstance.removeUserFromEditing.subscribe(
      (selectedUser: User) => {
        dialogRef.close();
        this.removeUser(selectedUser);
      },
    );
  }

  getCurrentEntitlementsForTin(tin: TinAccess, user: EditedUser) {
    const activeTin = user.accessAfter.find((tinObj) => tinObj.tin === tin.tin);
    return activeTin && activeTin.entitlements ? activeTin.entitlements : [];
  }

  diffEntitlements(tin: TinAccess, user: EditedUser): boolean {
    const accessBefore = user.accessBefore.find(
      (tinObj) => tinObj.tin === tin.tin,
    );
    const accessAfter = user.accessAfter.find(
      (tinObj) => tinObj.tin === tin.tin,
    );
    return !isEqual(
      accessBefore ? accessBefore.entitlements : [],
      accessAfter ? accessAfter.entitlements : [],
    );
  }

  applyEntitlementsByJobRole(tin: TinAccess, user: EditedUser) {
    this.manageAccessFacade.applyEntitlementsByJobRole({ tin, user });
  }

  revertToExistingAccess(tin: TinAccess, user: EditedUser) {
    this.manageAccessFacade.revertEntitlementsToExisting({ tin, user });
  }

  entitlementsChange(
    entitlements: EntitlementRole[],
    tin: TinAccess,
    user: EditedUser,
  ) {
    this.manageAccessFacade.updatedEditedUserIndividual({
      entitlements,
      tin,
      user,
    });
  }

  previousScreen() {
    this.router.navigate(['../assignment-method'], {
      relativeTo: this.activatedRoute,
    });
  }

  nextScreen() {
    this.router.navigate(['../review'], {
      relativeTo: this.activatedRoute,
    });
  }
}
